import React, {useCallback, useContext, useEffect, useMemo, useRef, useState} from 'react';
import {useNavigate, useParams, useSearchParams} from 'react-router-dom';

import {SwapMainAppContext} from '../../../context/SwapMainAppContext';
import {PRISMAX_DATA_PIPELINE_URL} from '../../../configs/config';
import {getApiErrorMessage, isSessionExpiredError} from '../../../utils/authErrors';
import {isQaRole, resolveQaRole} from '../../../utils/userRoles';

import TooltipQARequirements from './TooltipQARequirements';
import ValidatorAccessModal from './Modals/ValidatorAccessModal';
import SelectionModal from './Modals/SelectionModal';
import ScoringModal from './Modals/ScoringModal';
import EarningsModal from './Modals/EarningsModal';
import QAReviewSuccessModal from './Modals/QAReviewSuccessModal';
import LeaveQAReviewModalV2 from './Modals/LeaveQAReviewModalV2';
import RushQAReviewModal from './Modals/RushQAReviewModal';
import RateEpisodeVariationModal from './Modals/RateEpisodeVariationModal';
import CustomNotification from './CustomNotification';
import useFirefoxVideoSupportNotification from './useFirefoxVideoSupportNotification';
import useLeaveQAReviewGuard from './useLeaveQAReviewGuard';
import MotionDataPanel from './MotionDataPanel';

import arrowRightIcon from '../../../static_files/ep_arrow-right-bold.svg';
import styles from './DataQAReview.module.css';


const SPEED_OPTIONS = [0.5, 1, 1.5, 2];

export const MANUAL_QA_REQUIREMENTS = [
    {
        key: 'is_camera_feed_clear',
        label: "Clear camera feed",
        tooltip: "Is the footage visible throughout? Flag any blackouts, white flashes, or flickering.",
        kind: 'gate',
        desc: {
            definition: 'No all-black or all-white screens; no periodic brightness flicker throughout the episode.',
        },
    },
    {
        key: 'is_task_completed',
        label: "Task completed as instructed",
        tooltip: "Did the robot do what the scenario asked and finish in the right state? Minor slip-ups are fine if quickly corrected.",
        kind: 'gate',
        desc: {
            definition: 'Performs as the scenario instructs and reaches the intended final state. If a mistake occurs, the operator quickly recovers.',
        },
    },
    {
        key: 'is_robot_hand_visible',
        label: "Robot hand stays in frame",
        tooltip: "Is the robot's hand fully visible the whole time? It should never leave the camera frame.",
        kind: 'gate',
        desc: {
            definition: 'The end-effector shall remain fully within the camera field of view for the entire duration of the task.',
        },
    },
    {
        key: 'are_cameras_in_sync',
        label: "All cameras in sync",
        tooltip: "Do all camera feeds move in lockstep? Look for any lag or timing mismatch between views.",
        kind: 'gate',
        desc: {
            definition: 'All cameras are hardware synchronized with no temporal misalignment across cameras.',
        },
    },

    {
        key: 'robot_control_quality',
        label: "Robot control quality",
        tooltip: "How well the operator handles the robot. Confident, deliberate control makes better training data.",
        kind: 'score',
        desc: {
            definition: 'The ability of an operator to control the robot to collect data.',
            minimum: 'Operator is sufficiently skilled to produce data that meets the minimum requirements below.',
            recommended: 'Operator should pass a manipulation skills assessment consisting of common manipulations performed in demonstrations.',
            icon: (
                <svg viewBox="0 0 13 13" fill="none">
                    <circle cx="6.5" cy="5" r="2.5" stroke="#f0a04b" strokeWidth="1.1" />
                    <path d="M2.5 11c0-2.2 1.8-4 4-4s4 1.8 4 4" stroke="#f0a04b" strokeWidth="1.1" strokeLinecap="round" />
                </svg>
            ),
            iconBackground: 'rgba(240, 160, 75, 0.12)',
        },
    },
    {
        key: 'movement_smoothness',
        label: "Movement smoothness",
        tooltip: "Are the arm movements fluid? Look for continuous motion without sudden stops or jerks.",
        kind: 'score',
        desc: {
            definition: 'How smoothly the robot arm moves as it accomplishes tasks.',
            minimum: 'Trajectories complete each commanded motion without abrupt stops or stutters, tolerating brief pauses and minor jerk so long as no kinematic singularity is triggered.',
            recommended: 'Trajectories are continuous, fluid, and free of unnecessary pauses while avoiding configurations that induce kinematic singularities.',
            icon: (
                <svg viewBox="0 0 13 13" fill="none">
                    <path d="M1.5 9 C3 9 3 4 5 4 S7 8 9 8 S11 5 11.5 5" stroke="#7ab3f0" strokeWidth="1.1" strokeLinecap="round" fill="none" />
                </svg>
            ),
            iconBackground: 'rgba(122, 179, 240, 0.12)',
        },
    },
    {
        key: 'task_completion_speed',
        label: "Task completion speed",
        tooltip: "Does the task get done at a sensible pace? Too slow or too rushed both reduce data quality.",
        kind: 'score',
        desc: {
            definition: 'The amount of time it takes to complete a demonstration while maintaining compliance with all other quality standards (e.g. smoothness, completeness, non-robot actions, etc).',
            minimum: 'Demonstrations are efficient enough to be practical and beneficial for a robot end user.',
            recommended: 'Demonstrations are completed at an efficient, consistent pace that closely reflects optimal task execution, maximizing value for a robot end user.',
            icon: (
                <svg viewBox="0 0 13 13" fill="none">
                    <circle cx="6.5" cy="6.5" r="4.5" stroke="#f0a04b" strokeWidth="1.1" />
                    <path d="M6.5 4v2.5l1.5 1.5" stroke="#f0a04b" strokeWidth="1.1" strokeLinecap="round" />
                </svg>
            ),
            iconBackground: 'rgba(240, 160, 75, 0.12)',
        },
    },
    {
        key: 'task_fully_completed',
        label: "Task fully completed",
        tooltip: "Did the robot finish the task as intended? Small mistakes are fine if the operator recovers quickly.",
        kind: 'score',
        desc: {
            definition: 'The final outcome of a demonstration.',
            minimum: 'Demonstrations reach the intended final state of the task, as defined by the instructions. Operators quickly recover from any mistakes made during the demonstration.',
            recommended: 'Demonstrations reach the intended final state of the task cleanly, as defined by the instructions, with operators executing the task efficiently and without mistakes requiring recovery.',
            icon: (
                <svg viewBox="0 0 13 13" fill="none">
                    <circle cx="6.5" cy="6.5" r="4.5" stroke="#60c87a" strokeWidth="1.1" />
                    <path d="M4.5 6.5l1.5 1.5 2.5-2.5" stroke="#60c87a" strokeWidth="1.1" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
            ),
            iconBackground: 'rgba(96, 200, 122, 0.12)',
        },
    },
    {
        key: 'variation_across_episodes',
        label: "Variation across episodes",
        tooltip: "Does this episode feel different from others? Varied setups, objects, and approaches make the dataset more useful.",
        kind: 'score',
        uploadLevel: true, // variation_across_episodes is scored at the end of the review session in a separate modal
        desc: {
            definition: 'Differences in demonstrations across episodes that prevent redundancy and improve generalization.',
            minimum: 'Demonstrations with the same values across all diversity categories repeat no more than 20 times.',
            recommended: 'Each demonstration is unique — at least one diversity category is varied per demo.',
            icon: (
                <svg viewBox="0 0 13 13" fill="none">
                    <circle cx="3.5" cy="6.5" r="2" stroke="#7ab3f0" strokeWidth="1.1" />
                    <circle cx="9.5" cy="3.5" r="2" stroke="#7ab3f0" strokeWidth="1.1" />
                    <circle cx="9.5" cy="9.5" r="2" stroke="#7ab3f0" strokeWidth="1.1" />
                    <path d="M5.4 5.6l2.7-1.4M5.4 7.4l2.7 1.4" stroke="#7ab3f0" strokeWidth="1" strokeLinecap="round" />
                </svg>
            ),
            iconBackground: 'rgba(122, 179, 240, 0.12)',
            noteLabel: 'Diversity categories',
            // note: 'Environment · Scene · Demonstration · Objects (primary & distractor) · Actions',
            note: '\u00a0Environment\u00a0 · \u00a0Scene\u00a0 · \u00a0Demonstration \u00a0· \u00a0Objects (primary & distractor)\u00a0 · \u00a0Actions',
        },
    },
];

export const SCORE_REQUIREMENTS = MANUAL_QA_REQUIREMENTS.filter((item) => item.kind === 'score');
export const GATE_REQUIREMENTS = MANUAL_QA_REQUIREMENTS.filter((item) => item.kind === 'gate');
export const EPISODE_SCORE_REQUIREMENTS = SCORE_REQUIREMENTS.filter((item) => !item.uploadLevel);

const SCORE_LEVEL_OPTIONS = [
    {value: 1, label: '1 - Poor'},
    {value: 2, label: '2 - Weak'},
    {value: 3, label: '3 - Acceptable'},
    {value: 4, label: '4 - Good'},
    {value: 5, label: '5 - Excellent'},
];

const EXPERT_QA_INITIAL_EPISODE_CHUNK_SIZE = 20;
const EPISODE_CHUNK_SIZE = 5;

function createDefaultRubric() {
    return MANUAL_QA_REQUIREMENTS.reduce((acc, item) => {
        acc[item.key] = '';
        return acc;
    }, {});
}

function normalizeScoreValue(value) {
    if (value === '' || value === null || value === undefined) return '';
    const parsed = Number(value);
    if (!Number.isFinite(parsed)) return '';
    return Math.max(1, Math.min(5, parsed));
}

function normalizeGateValue(value) {
    const normalized = String(value || '').trim().toLowerCase();
    if (normalized === 'pass' || normalized === 'fail') return normalized;
    return '';
}

/* Apply bankers rounding for the final QA score calculation to match backend rounding */
function bankersRound(value) {
    const floor = Math.floor(value);
    const diff = value - floor;
    if (diff < 0.5) return floor;
    if (diff > 0.5) return floor + 1;
    return floor % 2 === 0 ? floor : floor + 1;
}

function calculateEpisodeRubricResult(rubricInput) {
    const rubric = {
        ...createDefaultRubric(),
        ...(rubricInput || {}),
    };

    const missingScoreKeys = [];
    const missingGateKeys = [];
    const failedGateKeys = [];
    let scoreSum = 0;

    EPISODE_SCORE_REQUIREMENTS.forEach((item) => {
        const scoreValue = normalizeScoreValue(rubric[item.key]);
        if (scoreValue === '') {
            missingScoreKeys.push(item.key);
            return;
        }
        scoreSum += scoreValue;
    });

    SCORE_REQUIREMENTS.forEach((item) => {
        if (item.uploadLevel) {
            const scoreValue = normalizeScoreValue(rubric[item.key]);
            if (scoreValue !== '') {
                scoreSum += scoreValue;
            }
        }
    });

    GATE_REQUIREMENTS.forEach((item) => {
        const gateValue = normalizeGateValue(rubric[item.key]);
        if (!gateValue) {
            missingGateKeys.push(item.key);
            return;
        }
        if (gateValue === 'fail') {
            failedGateKeys.push(item.key);
        }
    });

    const ready = missingScoreKeys.length === 0 && missingGateKeys.length === 0;
    const episodeScore = Math.round((scoreSum / (5 * SCORE_REQUIREMENTS.length)) * 100);
    const gatePassed = failedGateKeys.length === 0;
    const episodeVote = !ready ? 'pending' : (gatePassed ? 'pass' : 'fail');

    return {
        rubric,
        ready,
        episodeScore,
        gatePassed,
        episodeVote,
        missingScoreKeys,
        missingGateKeys,
        failedGateKeys,
    };
}

function buildUploadNote(sampledEpisodeIds, reviewState) {
    const lines = [];
    (sampledEpisodeIds || []).forEach((episodeId) => {
        const note = String(reviewState?.[episodeId]?.note || '').trim();
        if (!note) return;
        lines.push(`Episode ${episodeId}: ${note}`);
    });
    return lines.join('\n');
}

function buildUploadReviewPayload(uploadId, sampledEpisodeIds, reviewState, variationScore = null) {
    const normalizedEpisodeIds = (sampledEpisodeIds || [])
        .map((episodeId) => Number(episodeId))
        .filter((episodeId) => Number.isFinite(episodeId));
    if (!normalizedEpisodeIds.length) {
        return {
            ready: false,
            missingByEpisode: [],
            uploadScore: 0,
            uploadVote: 'pending',
            reviewResult: null,
        };
    }

    const episodeResults = normalizedEpisodeIds.map((episodeId) => {
        const episodeState = reviewState?.[episodeId] || {};
        const baseRubric = episodeState.rubric || {};
        const rubricWithVariation = variationScore !== null
            ? { ...baseRubric, variation_across_episodes: variationScore }
            : baseRubric;
        const rubricResult = calculateEpisodeRubricResult(rubricWithVariation);

        const gate = {};
        GATE_REQUIREMENTS.forEach((item) => {
            gate[item.key] = normalizeGateValue(rubricResult.rubric[item.key]) || 'fail';
        });

        const score = {};
        SCORE_REQUIREMENTS.forEach((item) => {
            score[item.key] = normalizeScoreValue(rubricResult.rubric[item.key]) || 1;
        });

        return {
            episodeId,
            rubricResult,
            payload: {
                episode_id: episodeId,
                gate,
                score,
                episode_vote: rubricResult.episodeVote,
                episode_score: rubricResult.episodeScore,
            },
        };
    });

    const missingByEpisode = episodeResults
        .filter((item) => !item.rubricResult.ready)
        .map((item) => item.episodeId);
    const ready = missingByEpisode.length === 0;
    const uploadScore = ready
        ? bankersRound(
            episodeResults.reduce((sum, item) => sum + item.rubricResult.episodeScore, 0) / episodeResults.length
        )
        : 0;
    const uploadVote = !ready
        ? 'pending'
        : (episodeResults.every((item) => item.rubricResult.gatePassed) ? 'pass' : 'fail');

    const reviewResult = {
        rubric_version: 'v2',
        upload_id: Number(uploadId),
        sampled_episode_id: normalizedEpisodeIds,
        result: episodeResults.map((item) => item.payload),
        upload_vote: uploadVote,
        upload_score: uploadScore,
    };

    return {
        ready,
        missingByEpisode,
        uploadScore,
        uploadVote,
        reviewResult,
    };
}

function formatDate(value) {
    if (!value) return '-';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return String(value);
    return date.toLocaleString();
}

function formatTime(seconds) {
    if (!Number.isFinite(seconds) || seconds < 0) return '00:00';
    const total = Math.floor(seconds);
    const h = Math.floor(total / 3600);
    const m = Math.floor((total % 3600) / 60);
    const s = total % 60;
    if (h > 0) {
        return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
    }
    return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

function getVideoLabel(video, fallbackIndex = 0) {
    const objectName = String(video?.object_name || '').trim();
    if (!objectName) return `camera_${fallbackIndex + 1}`;
    const filename = objectName.split('/').pop() || objectName;
    return filename.replace(/\.mp4$/i, '') || `camera_${fallbackIndex + 1}`;
}

function getObjectFilename(objectName, fallbackText = '') {
    const full = String(objectName || '').trim();
    if (!full) return fallbackText;
    return full.split('/').pop() || fallbackText;
}

function getStableHash(text) {
    let hash = 0;
    const value = String(text || '');
    for (let index = 0; index < value.length; index += 1) {
        hash = ((hash * 31) + value.charCodeAt(index)) >>> 0;
    }
    return hash;
}

function sortEpisodeVideos(videos, episodeId) {
    const list = Array.isArray(videos) ? [...videos] : [];
    if (list.length !== 3) return list;

    const decorated = list.map((video, index) => {
        const filename = getObjectFilename(video?.object_name, '').toLowerCase();
        const hasLeft = filename.includes('left');
        const hasRight = filename.includes('right');
        return {
            video,
            index,
            hasLeft,
            hasRight,
            randomRank: getStableHash(`${episodeId || 'episode'}:${filename}:${index}`),
        };
    });

    const matchedSide = decorated.some((item) => item.hasLeft || item.hasRight);
    if (!matchedSide) {
        return decorated
            .sort((a, b) => a.randomRank - b.randomRank || a.index - b.index)
            .map((item) => item.video);
    }

    return decorated
        .sort((a, b) => {
            const aRank = a.hasLeft && !a.hasRight ? 0 : (a.hasRight && !a.hasLeft ? 2 : 1);
            const bRank = b.hasLeft && !b.hasRight ? 0 : (b.hasRight && !b.hasLeft ? 2 : 1);
            if (aRank !== bRank) return aRank - bRank;
            return a.index - b.index;
        })
        .map((item) => item.video);
}

function getEpisodeState(reviewState, episodeId) {
    return reviewState[episodeId] || {
        speed: 1,
        note: '',
        rubric: createDefaultRubric(),
        sampleIndex: 0,
    };
}

function mergeEpisodesById(existing, incoming) {
    const byId = new Map((existing || []).map((episode) => [episode?.episode_id, episode]));
    (incoming || []).forEach((episode) => {
        if (!episode || episode.episode_id === undefined || episode.episode_id === null) return;
        byId.set(episode.episode_id, episode);
    });
    return Array.from(byId.values()).sort((a, b) => Number(a?.episode_id || 0) - Number(b?.episode_id || 0));
}

const ValidatorStatus = ({ validatorAccessData, onSelectionClick, onScoringClick, onEarningsClick }) => {
    const { userClass, userRole, qaRole } = useContext(SwapMainAppContext);
    const normalizedQaRole = resolveQaRole(qaRole, userRole);
    const isQa = isQaRole(normalizedQaRole);

    // Set config used to customize the UI styling based on user role / user class
    let config = null;
    if (isQa) {
        config = {
            label: (
                normalizedQaRole === 'senior qa' ? 'Senior Validator'
                : normalizedQaRole === 'expert qa' ? 'Expert Validator'
                : 'Validator'
            ),
            pipCount: 30,
            badgeCls: 'valTierBadgeQA',
            dotCls: 'valTierDotQA',
            pipDoneCls: 'valPipDoneInnovator',
        }
    } else if (userClass === 'Innovator Member') {
        config = {
            label: 'Innovator',
            pipCount: 30,
            badgeCls: 'valTierBadgeInnovator',
            dotCls: 'valTierDotInnovator',
            pipDoneCls: 'valPipDoneInnovator',
        };
    } else if (userClass === 'Amplifier Member') {
        config = {
            label: 'Amplifier',
            pipCount: 10,
            badgeCls: 'valTierBadgeAmplifier',
            dotCls: 'valTierDotAmplifier',
            pipDoneCls: 'valPipDoneAmplifier',
        };
    } else if (userClass === 'Explorer Member') {
        config = {
            label: 'Explorer',
            pipCount: 30,
            badgeCls: 'valTierBadgeExplorer',
            dotCls: 'valTierDotExplorer',
            pipDoneCls: null,
        };
    }

    if (!config) return null;

    const hasValidatorAccessData = validatorAccessData !== null;
    const spotsRemaining = validatorAccessData?.spots_remaining;
    const spotsTotal = validatorAccessData?.spots_total;
    const reviewsDone = validatorAccessData?.reviews_done ?? 0;
    const isExplorer = userClass === 'Explorer Member';
    const pipDoneCount = isExplorer ? 0 : Math.min(reviewsDone, config.pipCount);

    return (
        <div className={styles.valStatus}>
            <div className={styles.valSectionLabel}>Validator status</div>

            <div className={styles.valTierRow}>
                <div className={`${styles.valTierBadge} ${styles[config.badgeCls]}`}>
                    <span className={`${styles.valTierDot} ${styles[config.dotCls]}`} />
                    {config.label}
                </div>
                {(!isExplorer && !isQa)  && (
                    <span className={styles.valSpots}>
                        {hasValidatorAccessData ? `${spotsRemaining} / ${spotsTotal}` : '– / –'} spots open
                    </span>
                )}
            </div>
            <div className={styles.valProgressWrap}>
                {!isQa &&                
                <div className={styles.valProgressRow}>
                    <div className={styles.valPips}>
                        {Array.from({ length: config.pipCount }, (_, i) => (
                            <div
                                key={i}
                                className={`${styles.valPip}${i < pipDoneCount && config.pipDoneCls ? ` ${styles[config.pipDoneCls]}` : ''}`}
                            />
                        ))}
                    </div>
                    <span className={styles.valProgressLabel}>
                        {isExplorer ? '0 done' 
                        : (hasValidatorAccessData ? `${reviewsDone} / ${config.pipCount} done` : '– done')}
                    </span>
                </div>
                }
                {isQa ? (
                    <span className={styles.valProgressLabel}>
                        {hasValidatorAccessData
                            ? `${reviewsDone} review${reviewsDone === 1 ? '' : 's'} completed this month`
                            : '– reviews completed this month'}
                    </span>
                ) : (
                    <span className={styles.valProgressSub}>Reviews completed this month</span> 
                )}
            </div>

            <div className={styles.valLinks}>
                <button type="button" className={styles.valLink} onClick={onSelectionClick}>
                    <svg width="11" height="11" viewBox="0 0 11 11" fill="none">
                        <path d="M5.5 1A4.5 4.5 0 1 1 1 5.5" stroke="currentColor" strokeWidth="1.1" strokeLinecap="round"/>
                        <path d="M5.5 3.5v2.5l1.5 1" stroke="currentColor" strokeWidth="1.1" strokeLinecap="round"/>
                    </svg>
                    How selection works ↗
                </button>
                <button type="button" className={styles.valLink} onClick={onScoringClick}>
                    <svg width="11" height="11" viewBox="0 0 11 11" fill="none">
                        <path d="M1.5 9h8M3 9V5.5M5.5 9V3M8 9V6.5" stroke="currentColor" strokeWidth="1.1" strokeLinecap="round"/>
                    </svg>
                    View scoring rules ↗
                </button>
                <button type="button" className={styles.valLink} onClick={onEarningsClick}>
                    <svg width="11" height="11" viewBox="0 0 11 11" fill="none">
                        <circle cx="5.5" cy="5.5" r="4" stroke="currentColor" strokeWidth="1.1"/>
                        <path d="M5.5 3.5v.8M5.5 6.8v.7M4 5.1c0-.6.7-1 1.5-1s1.5.4 1.5 1-.7 1-1.5 1-1.5.4-1.5 1 .7 1 1.5 1" stroke="currentColor" strokeWidth="1.1" strokeLinecap="round"/>
                    </svg>
                    My earnings ↗
                </button>
            </div>
        </div>
    );
};

const ScenarioDropdown = ({ value, options, onChange }) => {
    const [open, setOpen] = useState(false);
    const wrapRef = useRef(null);
    const selected = options.find(t => t.task_id === value);

    useEffect(() => {
        if (!open) return;
        const handleClick = (e) => {
            if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener('mousedown', handleClick);
        return () => document.removeEventListener('mousedown', handleClick);
    }, [open]);

    return (
        <div className={styles.scenarioSelectWrap} ref={wrapRef}>
            <button
                type="button"
                className={`${styles.scenarioTrigger}${open ? ` ${styles.scenarioTriggerOpen}` : ''}`}
                onClick={() => setOpen(o => !o)}
            >
                <span className={styles.scenarioTriggerText}>{selected?.scenario ?? '—'}</span>
                <svg className={styles.scenarioChevron} width="10" height="10" viewBox="0 0 10 10" fill="none">
                    <path d="M2 3.5L5 6.5L8 3.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
            </button>
            {open && (
                <div className={styles.scenarioMenu}>
                    {options.map(task => (
                        <button
                            key={task.task_id}
                            type="button"
                            className={`${styles.scenarioOption}${task.task_id === value ? ` ${styles.scenarioOptionActive}` : ''}`}
                            onClick={() => {
                                if (task.task_id === value) {
                                    setOpen(false);
                                    return;
                                }
                                onChange({ target: { value: task.task_id } });
                                setOpen(false);
                            }}
                        >
                            {task.scenario}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}

const UploadDropdown = ({ value, options, onChange }) => {
    const [open, setOpen] = useState(false);
    const wrapRef = useRef(null);
    const selected = options.find((upload) => Number(upload?.upload_id) === Number(value));

    useEffect(() => {
        if (!open) return;
        const handleClick = (e) => {
            if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener('mousedown', handleClick);
        return () => document.removeEventListener('mousedown', handleClick);
    }, [open]);

    return (
        <div className={styles.scenarioSelectWrap} ref={wrapRef}>
            <button
                type="button"
                className={`${styles.scenarioTrigger}${open ? ` ${styles.scenarioTriggerOpen}` : ''}`}
                onClick={() => setOpen(prev => !prev)}
            >
                <span className={styles.scenarioTriggerText}>
                    {selected?.upload_id ? `Upload #${selected.upload_id}` : 'Upload'}
                </span>
                <svg className={styles.scenarioChevron} width="10" height="10" viewBox="0 0 10 10" fill="none">
                    <path d="M2 3.5L5 6.5L8 3.5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
            </button>

            {open && (
                <div className={styles.scenarioMenu}>
                    {options.map((upload) => (
                        <button
                            type="button"
                            key={upload.upload_id}
                            className={`${styles.scenarioOption}${upload.upload_id === value ? ` ${styles.scenarioOptionActive}` : ''}`}
                            onClick={() => {
                                if (upload.upload_id === value) {
                                    setOpen(false);
                                    return;
                                }
                                onChange({ target: { value: upload.upload_id } });
                                setOpen(false);
                            }}
                        >
                            {`Upload #${upload.upload_id}`}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
};


const DataQAReview = ({qaUploads = null, setQaUploads, skipUploadsFetch = false, dataTasks = [] }) => {
    const {
        gatewayToken,
        showNotification,
        userRole,
        qaRole,
        userClass,
        setQaRole,
        setUserClass,
        userSettings,
        setUserSettings,
        handleUserSessionExpired,
        setIsQaReviewInProgress,
        setRequestLeaveQaReviewConfirmation
    } = useContext(SwapMainAppContext);
    const {taskId} = useParams();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();

    const [selectedUploadId, setSelectedUploadId] = useState(null);
    const [episodes, setEpisodes] = useState([]);
    const [sampledEpisodeIds, setSampledEpisodeIds] = useState([]);
    const [qaRound, setQaRound] = useState(null);
    const [totalEpisodes, setTotalEpisodes] = useState(0);

    const [loadingUploads, setLoadingUploads] = useState(false);
    const [loadingEpisodes, setLoadingEpisodes] = useState(false);
    const [submittingUploadReview, setSubmittingUploadReview] = useState(false);
    const [loadingNextUploadAfterSubmit, setLoadingNextUploadAfterSubmit] = useState(false);
    const [locallySubmittedEpisodeIds, setLocallySubmittedEpisodeIds] = useState({});

    const [reviewState, setReviewState] = useState({});
    const [activeTab, setActiveTab] = useState('scoring');
    const [currentEpisodeIndex, setCurrentEpisodeIndex] = useState(0);
    const [isPlaying, setIsPlaying] = useState(false);
    const [isMuted, setIsMuted] = useState(false);
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(0);
   

    // State used for supporting the different modals (display, functionality, etc.)
    const dontShowAgainValidatorAccessModal = userSettings?.dont_show_again_validator_access_modal;
    const [validatorAccessData, setValidatorAccessData] = useState(null);
    const [showValidatorAccessModal, setShowValidatorAccessModal] = useState(false);
    const [showSelectionModal, setShowSelectionModal] = useState(false);
    const [showScoringModal, setShowScoringModal] = useState(false);
    const [showEarningsModal, setShowEarningsModal] = useState(false);
    const [showQAReviewSuccessModal, setShowQAReviewSuccessModal] = useState(false);
    const [showRushQAReviewModal, setShowRushQAReviewModal] = useState(false);
    const [showRateEpisodeVariationModal, setShowRateEpisodeVariationModal] = useState(false);
    const variationScoreRef = useRef(null);
    const episodeWatchedSecondsRef = useRef({});  // { [episodeId]: totalSecondsWatched }
    const episodePlayStartRef = useRef(null);     // timestamp when current play segment started, null if paused
    const prevEpisodeIdRef = useRef(null);
    
    // MCAP motion data panel state
    const [motionOpen, setMotionOpen] = useState(false);
    const [jointStatesCache, setJointStatesCache] = useState({}); // { [episodeId]: { status: 'loading' | 'ready' | 'error', data } }
    const jointStatesRequestedRef = useRef(new Set()); // Prevents duplicate network requests per episode

    // Custom notification on successful review submission
    const [customNotification, setCustomNotification] = useState(null);
    // Firefox browser video support notification
    const enableFirefoxNotificationCheck = !loadingUploads && !loadingEpisodes && episodes.length > 0;
    const { node: firefoxNotificationNode } = useFirefoxVideoSupportNotification({ enableFirefoxNotificationCheck });

    const videoRefs = useRef([]);
    const videoFrameRef = useRef(null);
    const prefetchRunRef = useRef(0);
    const fetchedEpisodeIndicesRef = useRef(new Set()); // Prevents episodes from getting fetched more than once
    const episodesRef = useRef([]);


    const token = gatewayToken || localStorage.getItem('gatewayToken');
    const normalizedTaskId = String(taskId || '').trim();
    const normalizedUserRole = resolveQaRole(qaRole, userRole);
    const isQa = isQaRole(normalizedUserRole);
    const isExpertQa = normalizedUserRole === 'expert qa';
    const isAdvancedQa = isExpertQa;
    const requestedUploadId = useMemo(() => {
        const raw = searchParams.get('upload');
        if (!raw) return null;
        const parsed = Number(raw);
        return Number.isFinite(parsed) ? parsed : null;
    }, [searchParams]);

    const currentTask = dataTasks.find((dt) => dt?.task_id && dt.task_id === Number(normalizedTaskId));
    const currentScenario = currentTask?.scenario ?? '';
    const otherTasks = dataTasks
        .filter(dt => 
            dt?.scenario 
            && dt?.task_id 
            && dt.task_id !== Number(normalizedTaskId)
            && qaUploads.find(qa => dt.task_id === qa.task_id)
        ).sort((a, b) => a.scenario?.localeCompare(b.scenario))
        ?? [];
    
    const taskOptions = currentTask
        ? [currentTask, ...otherTasks] 
        : otherTasks;


    const updateReviewState = (episodeId, patch) => {
        setReviewState((prev) => {
            const previousEpisode = prev[episodeId] || {};
            const mergedRubric = {
                ...createDefaultRubric(),
                ...(previousEpisode.rubric || {}),
                ...(patch.rubric || {}),
            };
            return {
                ...prev,
                [episodeId]: {
                    speed: 1,
                    note: '',
                    rubric: createDefaultRubric(),
                    sampleIndex: 0,
                    ...previousEpisode,
                    ...patch,
                    rubric: mergedRubric,
                },
            };
        });
    };

    const fetchUploads = useCallback(async () => {
        if (!token) {
            showNotification?.('error', 'Missing gateway token. Please log in again.');
            return;
        }
        try {
            setLoadingUploads(true);
            const res = await fetch(`${PRISMAX_DATA_PIPELINE_URL}/data/qa/uploads`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });
            if (!res.ok) {
                const err = await res.json().catch(() => ({}));
                throw new Error(getApiErrorMessage(err, 'Failed to load QA uploads'));
            }
            const json = await res.json();
            setQaUploads?.(json.data || []);
        } catch (error) {
            console.error('Failed to fetch QA uploads:', error);
            if (isSessionExpiredError(error)) {
                handleUserSessionExpired?.();
                return;
            }
            showNotification?.('error', error.message || 'Failed to load QA uploads.');
        } finally {
            setLoadingUploads(false);
        }
    }, [token, showNotification, handleUserSessionExpired]);

    const fetchEpisodeChunk = useCallback(async (
        uploadId,
        {
            offset = 0,
            limit = 5,
            mode = 'append',
            silent = false,
        } = {}
    ) => {
        if (!token) {
            showNotification?.('error', 'Missing gateway token. Please log in again.');
            return null;
        }
        try {
            if (!silent) {
                setLoadingEpisodes(true);
            }
            const episodesUrl = new URL(`${PRISMAX_DATA_PIPELINE_URL}/data/qa/uploads/${uploadId}/episodes`);
            episodesUrl.searchParams.set('offset', String(offset));
            episodesUrl.searchParams.set('limit', String(limit));

            // Prevent duplicate fetch requests for the same episodes 
            if (fetchedEpisodeIndicesRef.current.has(offset)) {
                return null;
            }
            for (let episodeIndex = offset; episodeIndex < offset + limit; episodeIndex++) {
                fetchedEpisodeIndicesRef.current.add(episodeIndex);
            }

            const res = await fetch(episodesUrl.toString(), {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });
            if (!res.ok) {
                const err = await res.json().catch(() => ({}));
                throw new Error(getApiErrorMessage(err, 'Failed to load episodes'));
            }
            const json = await res.json();
            const items = json.data || [];
            const meta = json.meta || {};
            const totalCount = Number(meta.total_count ?? items.length);

            if (Number.isFinite(totalCount) && totalCount >= 0) {
                setTotalEpisodes(totalCount);
            }
            setQaRound(meta.qa_round ?? null);
            if (Array.isArray(meta.sampled_episode_id)) {
                setSampledEpisodeIds(meta.sampled_episode_id.map((id) => Number(id)).filter((id) => Number.isFinite(id)));
            }

            if (mode === 'replace') {
                setEpisodes(items);
                setReviewState({});
                setLocallySubmittedEpisodeIds({});
                setCurrentEpisodeIndex(0);
                setActiveTab('scoring');
            } else {
                setEpisodes((prev) => mergeEpisodesById(prev, items));
            }
            return {items, totalCount};
        } catch (error) {
            console.error('Failed to fetch episodes:', error);
            if (isSessionExpiredError(error)) {
                handleUserSessionExpired?.();
                return null;
            }
            showNotification?.('error', error.message || 'Failed to load episodes.');
            return null;
        } finally {
            if (!silent) {
                setLoadingEpisodes(false);
            }
        }
    }, [token, showNotification, handleUserSessionExpired]);

    const loadUploadEpisodes = useCallback(async (uploadId) => {
        if (!uploadId) return;

        const runId = prefetchRunRef.current + 1;
        prefetchRunRef.current = runId;
        fetchedEpisodeIndicesRef.current = new Set();
        episodeWatchedSecondsRef.current = {};
        episodePlayStartRef.current = null;

        setEpisodes([]);
        setSampledEpisodeIds([]);
        setQaRound(null);
        setTotalEpisodes(0);
        setLocallySubmittedEpisodeIds({});

        const firstChunk = await fetchEpisodeChunk(uploadId, {
            offset: 0,
            limit: EPISODE_CHUNK_SIZE,
            mode: 'replace',
            silent: false,
        });
        if (!firstChunk) return;

        try {
            const stored = JSON.parse(localStorage.getItem('qaReviewState') || '{}');
            const saved = stored[uploadId];
            if (saved?.reviewState) {
                setReviewState(saved.reviewState);
            }
            if (saved?.locallySubmittedEpisodeIds) {
                setLocallySubmittedEpisodeIds(saved.locallySubmittedEpisodeIds);
            }
        } catch (e) {
            // localStorage read failure is non-fatal
        }

        const totalCount = Number(firstChunk.totalCount || 0);
        const fetchLimit = isExpertQa ? Math.min(totalCount, EXPERT_QA_INITIAL_EPISODE_CHUNK_SIZE) : totalCount;
        if (!Number.isFinite(totalCount) || fetchLimit <= EPISODE_CHUNK_SIZE) return;

        let nextOffset = EPISODE_CHUNK_SIZE;
        while (nextOffset < fetchLimit) {
            if (prefetchRunRef.current !== runId) return;
            await fetchEpisodeChunk(uploadId, {
                offset: nextOffset,
                limit: EPISODE_CHUNK_SIZE,
                mode: 'append',
                silent: true,
            });
            nextOffset += EPISODE_CHUNK_SIZE;
        }
    }, [fetchEpisodeChunk, isExpertQa]);

    // Fetches MCAP joint-state data for the motion panel graph and caches it by episode id.
    // Skips the request entirely if that episode is already cached or currently loading.
    const fetchJointStates = useCallback((episodeId) => {
        if (!episodeId || !token) return;
        if (jointStatesRequestedRef.current.has(episodeId)) return; // fetch() already issued for this episode — skip
        jointStatesRequestedRef.current.add(episodeId);

        setJointStatesCache((prev) => ({
            ...prev,
            [episodeId]: {status: 'loading', data: null},
        }));

        fetch(`${PRISMAX_DATA_PIPELINE_URL}/data/qa/episodes/${episodeId}/joint-states`, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        })
            .then((res) => res.json())
            .then((json) => {
                if (json.success) {
                    setJointStatesCache((prev) => ({
                        ...prev,
                        [episodeId]: {status: 'ready', data: json.data},
                    }));
                } else {
                    jointStatesRequestedRef.current.delete(episodeId); // allow a retry on next request
                    setJointStatesCache((prev) => ({
                        ...prev,
                        [episodeId]: {status: 'error', data: null},
                    }));
                }
            })
            .catch((error) => {
                console.error('Failed to fetch joint states:', error);
                jointStatesRequestedRef.current.delete(episodeId); // allow a retry on next request
                setJointStatesCache((prev) => ({
                    ...prev,
                    [episodeId]: {status: 'error', data: null},
                }));
            });
    }, [token]);

    // Continue to preload episodes in the background for expert QA users only
    useEffect(() => {
        if (!isExpertQa) return;
        if (!selectedUploadId) return;
        if (episodes.length >= totalEpisodes && totalEpisodes > 0) return;
        if (episodes.length < EXPERT_QA_INITIAL_EPISODE_CHUNK_SIZE) return;
        if (currentEpisodeIndex + (2 * EPISODE_CHUNK_SIZE) <= episodes.length - 1) return;

        void fetchEpisodeChunk(selectedUploadId, {
            offset: episodes.length,
            limit: EPISODE_CHUNK_SIZE,
            mode: 'append',
            silent: true,
        });
    }, [currentEpisodeIndex, episodes.length, totalEpisodes, selectedUploadId, fetchEpisodeChunk, isExpertQa]);


    // Preload the data for the ValidatorAccessModal
    const fetchValidatorAccessData = useCallback(async () => {
        try {
            const res = await fetch(`${PRISMAX_DATA_PIPELINE_URL}/data/qa/get-validator-access-data`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
            });
            if (!res.ok) {
                const err = await res.json().catch(() => ({}));
                throw new Error(getApiErrorMessage(err, 'Failed to load validator access data'));
            }
            const json = await res.json();
            const result  = json?.data || null;
            setValidatorAccessData(result);

            if (result) {
                const incomingRole = ('qa_role' in result || 'user_role' in result)
                    ? resolveQaRole(result.qa_role, result.user_role)
                    : undefined;
                const incomingClass = result.user_class ?? null;
                if (incomingRole !== undefined && incomingRole !== qaRole) {
                    setQaRole(incomingRole);
                    if (incomingRole) localStorage.setItem('qaRole', incomingRole);
                    else localStorage.removeItem('qaRole');
                }
                if (incomingClass !== null && incomingClass !== userClass) {
                    setUserClass(incomingClass);
                    localStorage.setItem('userClass', incomingClass);
                }
            }
        } catch (error) {
            console.error('Failed to load validator access data:', error);
            if (isSessionExpiredError(error)) {
                handleUserSessionExpired?.();
            }
        }
    }, [token, handleUserSessionExpired, qaRole, userClass, setQaRole, setUserClass]);

    useEffect(() => {
        if (!validatorAccessData) {
            fetchValidatorAccessData();
        }
    }, [validatorAccessData, fetchValidatorAccessData])


    useEffect(() => {
        episodesRef.current = episodes;
    }, [taskId, episodes]);

    useEffect(() => {
        if (skipUploadsFetch) return;
        void fetchUploads();
    }, [taskId, fetchUploads, skipUploadsFetch]);

    const filteredUploads = useMemo(() => {
        const uploads = Array.isArray(qaUploads) ? qaUploads : [];
        if (!normalizedTaskId) return uploads;
        return uploads.filter((upload) => String(upload?.task_id || '') === normalizedTaskId);
    }, [qaUploads, normalizedTaskId]);

    useEffect(() => {
        if (loadingNextUploadAfterSubmit) {
            // Next upload is being loaded after submit. Avoid duplicate requests/conflicts until next upload is finished loading.
            return;
        }
        
        if (!filteredUploads.length) {
            setSelectedUploadId(null);
            setEpisodes([]);
            return;
        }
        
        const preferredUploadId = filteredUploads.some((upload) => upload.upload_id === requestedUploadId)
            ? requestedUploadId
            : filteredUploads[0].upload_id;

        if (preferredUploadId === selectedUploadId) return;
        if (requestedUploadId !== null && preferredUploadId !== requestedUploadId) {
            setSearchParams((prev) => {
                const next = new URLSearchParams(prev);
                next.set('upload', String(preferredUploadId));
                return next;
            }, { replace: true });
            showNotification?.('warning', "The requested upload was not available for review. Redirecting to a similar upload.");
        }

        setSelectedUploadId(preferredUploadId);

        void loadUploadEpisodes(preferredUploadId);
    }, [filteredUploads, requestedUploadId, selectedUploadId, loadUploadEpisodes, loadingNextUploadAfterSubmit]);

    const currentEpisode = episodes[currentEpisodeIndex] || null;
    const currentEpisodeId = currentEpisode?.episode_id;
    const currentEpisodeState = currentEpisodeId
        ? getEpisodeState(reviewState, currentEpisodeId)
        : getEpisodeState(reviewState, -1);
    const currentRubricResult = calculateEpisodeRubricResult(currentEpisodeState.rubric);

    const currentVideos = useMemo(
        () => sortEpisodeVideos(currentEpisode?.derived_videos || [], currentEpisode?.episode_id),
        [currentEpisode?.derived_videos, currentEpisode?.episode_id]
    );

    const preloadEpisodes = episodes.slice(currentEpisodeIndex + 1, currentEpisodeIndex + 3);
    const currentSampleIndex = Math.min(
        currentEpisodeState.sampleIndex || 0,
        Math.max(currentVideos.length - 1, 0)
    );
    const visibleVideoStartIndex = Math.min(
        currentSampleIndex,
        Math.max(currentVideos.length - 3, 0)
    );
    const displayedVideos = currentVideos.slice(visibleVideoStartIndex, visibleVideoStartIndex + 3);

    // Always default motion panel to closed on every episode change
    useEffect(() => {
        if (currentEpisodeId) {
            setMotionOpen(false);
        }
    }, [currentEpisodeId]);

    // Always ensure the current episode's motion data is loading/loaded
    useEffect(() => {
        if (currentEpisodeId) {
            fetchJointStates(currentEpisodeId);
        }
    }, [currentEpisodeId, fetchJointStates]);

    // Prefetch motion data for the upcoming episodes in the background
    useEffect(() => {
        const upcoming = episodes.slice(currentEpisodeIndex + 1, currentEpisodeIndex + 5);
        upcoming.forEach((ep) => {
            if (ep?.episode_id) {
                fetchJointStates(ep.episode_id);
            }
        });
    }, [currentEpisodeIndex, episodes, fetchJointStates]);

    useEffect(() => {
        // On episode change, flush any in-progress play segment into the previous episode's accumulated total.
        if (episodePlayStartRef.current !== null && prevEpisodeIdRef.current !== null) {
            const elapsed = (Date.now() - episodePlayStartRef.current) / 1000;
            episodeWatchedSecondsRef.current[prevEpisodeIdRef.current] =
                (episodeWatchedSecondsRef.current[prevEpisodeIdRef.current] || 0) + elapsed;
            episodePlayStartRef.current = null;
        }
        prevEpisodeIdRef.current = currentEpisodeId ?? null;
    }, [currentEpisodeId]);

    useEffect(() => {
        // Reset video playback to the beginning whenever the current video changes
        setCurrentTime(0);
        setIsPlaying(false);
    }, [currentVideos]);



    const shouldShowRoundTag = normalizedUserRole === 'expert qa' || normalizedUserRole === 'senior qa';
    const shouldShowUploadSelector = isAdvancedQa;
    const isLoadingContent = loadingNextUploadAfterSubmit || loadingUploads || loadingEpisodes || !selectedUploadId;

    const effectiveSampledEpisodeIds = useMemo(() => {
        if (isExpertQa) {
            return Object.keys(locallySubmittedEpisodeIds)
                .map((episodeId) => Number(episodeId))
                .filter((id) => Number.isFinite(id))
                .sort((left, right) => left - right);
        }
        if (sampledEpisodeIds.length > 0) return sampledEpisodeIds;
        return episodes.map((item) => Number(item.episode_id)).filter((id) => Number.isFinite(id));
    }, [episodes, isExpertQa, locallySubmittedEpisodeIds, sampledEpisodeIds]);
    const selectedBatchCount = effectiveSampledEpisodeIds.length;
    const isCurrentEpisodeInBatch = Boolean(currentEpisodeId && locallySubmittedEpisodeIds[currentEpisodeId]);
    const currentEpisodeSubmitReady = Boolean(currentEpisodeId) && currentRubricResult.ready;

    const uploadRubricResult = useMemo(() => {
        if (!selectedUploadId) {
            return {
                ready: false,
                missingByEpisode: [],
                uploadScore: 0,
                uploadVote: 'pending',
                reviewResult: null,
            };
        }
        return buildUploadReviewPayload(selectedUploadId, effectiveSampledEpisodeIds, reviewState);
    }, [selectedUploadId, effectiveSampledEpisodeIds, reviewState]);

    const getNextPendingEpisodeIndex = useCallback((submittedMap) => {
        const nextIndex = episodes.findIndex((episode) => !submittedMap[episode?.episode_id]);
        return nextIndex >= 0 ? nextIndex : currentEpisodeIndex;
    }, [episodes, currentEpisodeIndex]);

    const hasStartedCurrentBatch = useMemo(() => {
        return Object.values(reviewState || {}).some((episodeData) => {
            const rubric = episodeData?.rubric || {};
            return MANUAL_QA_REQUIREMENTS.some((item) => {
                const value = rubric[item.key];
                return value !== '' && value !== null && value !== undefined;
            });
        });
    }, [reviewState]);

    const {
        isLeaveModalOpen: isLeaveQAReviewModalOpen,
        guardNavigation: showLeaveQAReviewNotification,
        confirmLeave: confirmLeaveQAReview,
        cancelLeave: cancelLeaveQAReview,
    } = useLeaveQAReviewGuard({hasStartedCurrentBatch});

    // Expose the QA-review-in-progress guard to the side menu / mobile menu (outside this component's tree)
    useEffect(() => {
        setIsQaReviewInProgress(hasStartedCurrentBatch);
    }, [hasStartedCurrentBatch, setIsQaReviewInProgress]);

    useEffect(() => {
        setRequestLeaveQaReviewConfirmation(() => showLeaveQAReviewNotification);
        return () => {
            setIsQaReviewInProgress(false);
            setRequestLeaveQaReviewConfirmation(() => (onConfirm) => onConfirm?.());
        };
    }, [showLeaveQAReviewNotification, setRequestLeaveQaReviewConfirmation, setIsQaReviewInProgress]);

    // Warn on tab close/refresh while a QA review batch is in progress (in-app navigation is handled separately above)
    useEffect(() => {
        if (!hasStartedCurrentBatch) return;
        const handleBeforeUnload = (event) => {
            event.preventDefault();
            event.returnValue = '';
        };
        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => window.removeEventListener('beforeunload', handleBeforeUnload);
    }, [hasStartedCurrentBatch]);

    const handleChangeScenario = (event) => {
        const dest = event.target.value;
        if (showLeaveQAReviewNotification(
            () => navigate(`/data/review/${dest}`)
        )) return;
        navigate(`/data/review/${dest}`);
    };

    const handleChangeUpload = (event) => {
        const nextUploadId = Number(event.target.value);
        if (!Number.isFinite(nextUploadId) || nextUploadId === selectedUploadId) return;

        if (showLeaveQAReviewNotification(() => {
            setSelectedUploadId(nextUploadId);
            void loadUploadEpisodes(nextUploadId);
            navigate(`/data/review/${normalizedTaskId}?upload=${nextUploadId}`, {replace: true});
        })) return;
    
        setSelectedUploadId(nextUploadId);
        void loadUploadEpisodes(nextUploadId);
        navigate(`/data/review/${normalizedTaskId}?upload=${nextUploadId}`, {replace: true});
    };


    const handleBackToPreviousQa = () => {
        if (showLeaveQAReviewNotification(() => navigate('/data/review'))) return;
        navigate('/data/review');
    };

    const handlePrevEpisode = () => {
        setCurrentEpisodeIndex((prev) => Math.max(prev - 1, 0));
    };

    const handleNextEpisode = async () => {
        const totalCount = totalEpisodes || episodesRef.current.length;
        const maxTotalIndex = Math.max(totalCount - 1, 0);
        if (currentEpisodeIndex >= maxTotalIndex) return;

        const nextIndex = currentEpisodeIndex + 1;
        if (nextIndex >= episodesRef.current.length && selectedUploadId) {
            await fetchEpisodeChunk(selectedUploadId, {
                offset: episodesRef.current.length,
                limit: 5,
                mode: 'append',
                silent: true,
            });
        }

        const maxLoadedIndex = Math.max(episodesRef.current.length - 1, 0);
        setCurrentEpisodeIndex(Math.min(nextIndex, maxLoadedIndex));
    };

    const handleSpeedChange = (episodeId, speedValue) => {
        const speed = Number(speedValue);
        updateReviewState(episodeId, {speed});
        videoRefs.current.forEach((video) => {
            if (!video) return;
            video.playbackRate = speed;
        });
    };

    const cycleSpeed = () => {
        if (!currentEpisodeId) return;
        const currentSpeed = currentEpisodeState.speed || 1;
        const index = SPEED_OPTIONS.indexOf(currentSpeed);
        const nextSpeed = SPEED_OPTIONS[(index + 1) % SPEED_OPTIONS.length];
        handleSpeedChange(currentEpisodeId, nextSpeed);
    };

    const handleSampleChange = (sampleIndex) => {
        if (!currentEpisodeId) return;
        updateReviewState(currentEpisodeId, {sampleIndex});
        setCurrentTime(0);
        setDuration(0);
    };

    const handleRequirementChange = (episodeId, key, value) => {
        if (!episodeId) return;
        setLocallySubmittedEpisodeIds((prev) => {
            if (!prev[episodeId]) return prev;
            const next = {...prev};
            delete next[episodeId];
            return next;
        });
        updateReviewState(episodeId, {
            rubric: {
                [key]: value,
            },
        });
    };

    const handleLoadNextEpisodeAfterSubmit = async () => {
        setLoadingNextUploadAfterSubmit(true);
        const submittedTaskId = Number(normalizedTaskId);

        // Determine the next upload id and task id to navigate to
        let nextTaskId = Number(submittedTaskId);
        let nextUploadId;
        nextUploadId = filteredUploads.find((upload) => upload.upload_id !== selectedUploadId)?.upload_id;

        // Unable to select a different upload under the currently submitted task, select the first upload found under a different task
        if (nextUploadId == null) {
            const nextUploadAndTask = (Array.isArray(qaUploads) ? qaUploads : []).find((upload) =>
                upload?.task_id !== null
                && upload?.upload_id !== null
                && upload.task_id !== submittedTaskId
            );
            // Edge case. No uploads remaining in the entire app. Navigate back to the review hub
            if (!nextUploadAndTask) {
                setLoadingNextUploadAfterSubmit(false);
                return navigate('/data/review');
            }
            nextUploadId = nextUploadAndTask.upload_id;
            nextTaskId = nextUploadAndTask.task_id;
        }

        // Update the next upload id and task id
        navigate(`/data/review/${nextTaskId}?upload=${nextUploadId}`);
        setSelectedUploadId(nextUploadId);

        // Fetch the data for the next upload id
        void loadUploadEpisodes(nextUploadId);
        await fetchUploads();
        await new Promise(resolve => setTimeout(resolve, 1500)); // Introduce a small time delay to ensure the first loadUploadEpisodes API request has finished

        setLoadingNextUploadAfterSubmit(false);
    };

    const handleNoteChange = (episodeId, note) => {
        setLocallySubmittedEpisodeIds((prev) => {
            if (!prev[episodeId]) return prev;
            const next = {...prev};
            delete next[episodeId];
            return next;
        });
        updateReviewState(episodeId, {note});
    };

    const handleTriggerSubmitReview = async () => {
        if (!currentEpisodeId || submittingUploadReview) return;
        if (!currentEpisodeSubmitReady) {
            showNotification?.('warning', `Please finish all fields before submit. Missing episode: ${currentEpisodeId}`);
            return;
        }

        if (!isExpertQa) {
            if (episodePlayStartRef.current !== null) {
                await handleTogglePlay(); // pause the video if it is playing
            }
            const episodeDurationSeconds = currentEpisode?.video_duration_hours
                ? Math.round(parseFloat(currentEpisode.video_duration_hours) * 3600)
                : null;
            const rushThresholdSeconds = (episodeDurationSeconds !== null && episodeDurationSeconds < 30)
                ? episodeDurationSeconds
                : 30;
            const accumulatedSeconds = (episodeWatchedSecondsRef.current[currentEpisodeId] || 0);
            if (accumulatedSeconds < rushThresholdSeconds) {
                setShowRushQAReviewModal(true);
                return;
            }
        }

        if (userClass === 'Explorer Member' && !isQa) {
            setShowValidatorAccessModal(true);
            return;
        }

        const nextSubmittedMap = {
            ...locallySubmittedEpisodeIds,
            [currentEpisodeId]: true,
        };
        const allEpisodesLocallySubmitted = effectiveSampledEpisodeIds.length > 0
            && effectiveSampledEpisodeIds.every((episodeId) => nextSubmittedMap[episodeId]);

        setLocallySubmittedEpisodeIds(nextSubmittedMap);

        try {
            const stored = JSON.parse(localStorage.getItem('qaReviewState') || '{}');
            stored[selectedUploadId] = {
                reviewState,
                locallySubmittedEpisodeIds: nextSubmittedMap,
            };
            localStorage.setItem('qaReviewState', JSON.stringify(stored));
        } catch (e) {
            // localStorage write failure is non-fatal
        }

        if (isExpertQa) {
            const nextIndex = Math.min(currentEpisodeIndex + 1, Math.max(episodes.length - 1, 0));
            setCurrentEpisodeIndex(nextIndex);
            showNotification?.(
                'success',
                `Episode ${currentEpisodeId} added to batch. ${Object.keys(nextSubmittedMap).length} selected for final submission.`
            );
            return;
        }

        if (!allEpisodesLocallySubmitted) {
            const nextIndex = getNextPendingEpisodeIndex(nextSubmittedMap);
            setCurrentEpisodeIndex(nextIndex);
            showNotification?.('success', `Episode ${currentEpisodeId} saved locally. Final submission will happen after the last sampled episode.`);
            return;
        }

        setShowRateEpisodeVariationModal(true);
    }

    const handleSubmitUploadReview = async (updateDontShowAgainValidatorAccessModal = false) => {
        if (!selectedUploadId || submittingUploadReview) return;
        if (isExpertQa && selectedBatchCount <= 0) {
            showNotification?.('warning', 'Add at least one episode to the batch before final submit.');
            return;
        }
        const effectivePayload = buildUploadReviewPayload(
            selectedUploadId,
            effectiveSampledEpisodeIds,
            reviewState,
            variationScoreRef.current
        );
        if (!effectivePayload.ready) {
            const firstMissingEpisodeId = effectivePayload.missingByEpisode[0];
            showNotification?.('warning', `Please finish all fields before submit. Missing episode: ${firstMissingEpisodeId}`);
            return;
        }
        if (!effectivePayload.reviewResult) {
            showNotification?.('warning', 'No review payload generated.');
            return;
        }
        try {
            setSubmittingUploadReview(true);
            const note = buildUploadNote(effectiveSampledEpisodeIds, reviewState);

            const res = await fetch(`${PRISMAX_DATA_PIPELINE_URL}/data/qa/uploads/${selectedUploadId}/review`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({
                    qa_round: qaRound,
                    qa_score: effectivePayload.uploadScore,
                    note,
                    review_result: effectivePayload.reviewResult,
                    dont_show_again_validator_access_modal: updateDontShowAgainValidatorAccessModal
                }),
            });
            if (!res.ok) {
                const err = await res.json().catch(() => ({}));
                throw new Error(getApiErrorMessage(err, 'Failed to submit upload review'));
            }
            await res.json().catch(() => ({}));

            // Clear the variation score ref now that submission is complete
            variationScoreRef.current = null;

            // Update review count and don't show again for the validator access modal
            if (validatorAccessData) {
                setValidatorAccessData({
                    ...validatorAccessData,
                    reviews_done: (validatorAccessData?.reviews_done ?? 0) + 1
                });
            }
            if (updateDontShowAgainValidatorAccessModal) {
                setUserSettings({
                    ...userSettings,
                    dont_show_again_validator_access_modal: true
                });
            }

            // Give user success feedback. Show the notification or the qa review success modal. Load next episodes for review in callback.
            if (userSettings?.dont_show_again_qa_review_success_modal) {
                setCustomNotification({
                    title: 'Scores submitted',
                    description: 'Points will be granted once a consensus score is reached across all validators.',
                    duration: 4000,
                    onDismiss: handleLoadNextEpisodeAfterSubmit
                });
            } else {
                setShowQAReviewSuccessModal(true);
            }

        } catch (error) {
            console.error('Failed to submit upload review:', error);
            if (isSessionExpiredError(error)) {
                handleUserSessionExpired?.();
                return;
            }
            showNotification?.('error', error.message || 'Failed to submit upload review.');
        } finally {
            setSubmittingUploadReview(false);
            // If the submit handler was triggered from the modal, close the modal
            if (showValidatorAccessModal) {
                setShowValidatorAccessModal(false);
            }
        }
    };

    const handleTogglePlay = async () => {
        const renderedVideos = videoRefs.current.filter(Boolean);
        if (!renderedVideos.length) return;
        try {
            const shouldPlay = renderedVideos.some((video) => video.paused);
            if (shouldPlay) {
                episodePlayStartRef.current = Date.now();
                await Promise.allSettled(renderedVideos.map((video) => video.play()));
            } else {
                if (episodePlayStartRef.current !== null) {
                    const elapsed = (Date.now() - episodePlayStartRef.current) / 1000;
                    episodeWatchedSecondsRef.current[currentEpisodeId] =
                        (episodeWatchedSecondsRef.current[currentEpisodeId] || 0) + elapsed;
                    episodePlayStartRef.current = null;
                }
                renderedVideos.forEach((video) => video.pause());
            }
        } catch (error) {
            console.error('Failed to toggle play:', error);
        }
    };

    const handleToggleMute = () => {
        const renderedVideos = videoRefs.current.filter(Boolean);
        if (!renderedVideos.length) return;
        const nextMuted = !renderedVideos[0].muted;
        renderedVideos.forEach((video) => {
            video.muted = nextMuted;
        });
        setIsMuted(nextMuted);
    };

    const handleSeek = (event) => {
        const renderedVideos = videoRefs.current.filter(Boolean);
        if (!renderedVideos.length || !duration) return;
        const nextTime = Number(event.target.value);
        renderedVideos.forEach((video) => {
            video.currentTime = nextTime;
        });
        setCurrentTime(nextTime);
    };

    const handleMotionSeek = (nextTime) => {
        const renderedVideos = videoRefs.current.filter(Boolean);
        if (!renderedVideos.length || !Number.isFinite(nextTime)) return;
        renderedVideos.forEach((video) => {
            video.currentTime = nextTime;
        });
        setCurrentTime(nextTime);
    };

    const handleToggleFullscreen = async () => {
        const container = videoFrameRef.current;
        if (!container) return;
        try {
            if (document.fullscreenElement) {
                await document.exitFullscreen();
            } else {
                await container.requestFullscreen?.();
            }
        } catch (error) {
            console.error('Failed to toggle fullscreen:', error);
        }
    };

    useEffect(() => {
        if (!currentEpisodeId) return;
        const speed = currentEpisodeState.speed || 1;
        videoRefs.current.forEach((video) => {
            if (!video) return;
            video.playbackRate = speed;
        });
    }, [taskId, currentEpisodeId, visibleVideoStartIndex, currentEpisodeState.speed]);

    useEffect(() => {
        videoRefs.current = videoRefs.current.slice(0, displayedVideos.length);
    }, [taskId, displayedVideos.length]);

    const handleClearExpertQaBatch = () => {
        if (!isExpertQa) return;
        setLocallySubmittedEpisodeIds({});
        setReviewState({});
        try {
            const stored = JSON.parse(localStorage.getItem('qaReviewState') || '{}');
            delete stored[selectedUploadId];
            localStorage.setItem('qaReviewState', JSON.stringify(stored));
        } catch {
            // localStorage failure is not fatal
        }
       
        showNotification?.('success', 'Cleared current expert QA batch.');
    };

    const handleRemoveCurrentEpisodeFromBatch = () => {
        if (!isExpertQa || !currentEpisodeId) return;
        
        const newLocallySubmittedEpisodeIds = {...locallySubmittedEpisodeIds};
        delete newLocallySubmittedEpisodeIds[currentEpisodeId];
        setLocallySubmittedEpisodeIds(newLocallySubmittedEpisodeIds);

        const newReviewState = {...reviewState};
        delete newReviewState[currentEpisodeId];
        setReviewState(newReviewState);

        try {
            const stored = JSON.parse(localStorage.getItem('qaReviewState') || '{}');
            if (stored?.[selectedUploadId]) {
                stored[selectedUploadId] = {
                    reviewState: newReviewState,
                    locallySubmittedEpisodeIds: newLocallySubmittedEpisodeIds,
                };
            }
            localStorage.setItem('qaReviewState', JSON.stringify(stored));

        } catch {
            // localStorage failure is not fatal
        }

        showNotification?.('success', `Episode ${currentEpisodeId} removed from batch.`);
    };

    const handleFinalizeExpertQaBatch = async () => {
        if (!isExpertQa) return;
        setShowRateEpisodeVariationModal(true);
    };

    const handleVariationConfirm = async (variationScore) => {
        variationScoreRef.current = variationScore;
        setShowRateEpisodeVariationModal(false);
        if (dontShowAgainValidatorAccessModal || isQa) {
            await handleSubmitUploadReview();
        } else {
            setShowValidatorAccessModal(true);
        }
    };

    if (!isLoadingContent && !filteredUploads.length) {
        if (normalizedTaskId) {
            return (
                <div className={styles.status}>
                    No uploads found for task {normalizedTaskId}.
                    <button className={styles.statusBackBtn} onClick={() => navigate('/data/review')}>Back to Verify Quality</button>
                </div>
            );
        }
        return (
            <div className={styles.status}>
                No derived uploads ready for QA.
                <button className={styles.statusBackBtn} onClick={() => navigate('/data/review')}>Back to Verify Quality</button>
            </div>
        );
    }

    if (!isLoadingContent && !episodes.length) {
        return <div className={styles.status}>No sampled episodes found for this upload.</div>;
    }

    const gateVote = !currentRubricResult.ready ? '—' : (currentRubricResult.gatePassed ? 'Pass' : 'Fail');
    const gateClass = !currentRubricResult.ready ? '' : (currentRubricResult.gatePassed ? styles.statValGood : styles.statValBad);
    const voteVal = currentRubricResult.ready ? currentRubricResult.episodeVote : 'pending';
    const voteDisplay = voteVal === 'pending' ? '—' : voteVal.charAt(0).toUpperCase() + voteVal.slice(1);
    const voteClass = voteVal === 'pending' ? '' : (voteVal === 'pass' ? styles.statValGood : styles.statValBad);
    const scoreDisplay = isLoadingContent ? '—' : (currentRubricResult.ready ? currentRubricResult.episodeScore : 0);
    const progressPct = totalEpisodes > 0 ? Math.round(((currentEpisodeIndex + 1) / totalEpisodes) * 100) : 0;

    return (
        <>
        <div className={styles.viewerPage}>
            {/* Top bar */}
            <div className={styles.topBar}>
                <div className={styles.topBarLeft}>
                    <button
                        type="button"
                        className={styles.backBtn}
                        onClick={() => {
                            handleBackToPreviousQa();
                        }}
                    >
                        <img src={arrowRightIcon} alt="" className={styles.arrowLeftIcon} />
                    </button>
                    <button
                        type="button"
                        className={styles.breadcrumbLink}
                        onClick={handleBackToPreviousQa}
                    >
                        Verify quality
                    </button>
                    <span className={styles.breadcrumbSep}>/</span>
                    <ScenarioDropdown
                        value={Number(normalizedTaskId)}
                        options={taskOptions}
                        onChange={handleChangeScenario}
                    />
                    {shouldShowUploadSelector ? (
                        <>
                            <span className={styles.breadcrumbSep}>/</span>
                            {selectedUploadId ? (
                                <UploadDropdown
                                    value={Number(selectedUploadId)}
                                    options={filteredUploads}
                                    onChange={handleChangeUpload}
                                />
                            ) : (
                                <span className={`${styles.scenarioTrigger} ${styles.scenarioTriggerDisabled}`}>
                                    <span className={styles.scenarioTriggerText}>Upload</span>
                                </span>
                            )}
                        </>
                    ) : null}
                    <span className={styles.epSep} />
                    <span className={styles.epLabel}>Episode #{currentEpisodeId ?? '—'}</span>
                    {shouldShowRoundTag ? (
                        <span className={styles.roundTag}>Round {qaRound || '-'}</span>
                    ) : null}
                </div>
                <div className={styles.topBarRight}>
                    <div className={styles.epInfoMobile}>
                        <span className={styles.epLabelMobile}>Episode #{currentEpisodeId ?? '—'}</span>
                        {shouldShowRoundTag ? (
                            <span className={styles.roundTagMobile}>Round {qaRound || '-'}</span>
                        ) : null}
                    </div>
                    <div className={styles.epNavGroup}>
                        <button
                            type="button"
                            className={styles.navBtn}
                            onClick={handlePrevEpisode}
                            disabled={isLoadingContent || currentEpisodeIndex <= 0}
                        >
                            <img src={arrowRightIcon} alt="" className={styles.arrowLeftIconSmall} />
                        </button>
                        <span className={styles.epCounter}>{currentEpisodeIndex + 1} of {totalEpisodes || episodes.length || '—'}</span>
                        <button
                            type="button"
                            className={styles.navBtn}
                            onClick={handleNextEpisode}
                            disabled={isLoadingContent || currentEpisodeIndex >= (totalEpisodes || episodes.length) - 1}
                        >
                            <img src={arrowRightIcon} alt="" className={styles.arrowRightIconSmall} />
                        </button>
                    </div>
                </div>
            </div>

            {/* Progress bar */}
            <div className={styles.progressBar}>
                <div className={styles.progressFill} style={{width: `${progressPct}%`}} />
            </div>

            {/* Video viewer — stretches to fill remaining space */}
            <div className={styles.viewer} ref={videoFrameRef}>
                <div className={styles.camStrip}>
                    {isLoadingContent ? (
                        <>
                            {[0, 1, 2].map((paneIndex) => (
                                <div className={styles.camPane} key={`loading-cam-${paneIndex}`} />
                            ))}
                            <div className={styles.playOverlay}>
                                <svg className={styles.viewerSpinner} width="30" height="30" viewBox="0 0 30 30">
                                    <circle cx="15" cy="15" r="12" fill="none" stroke="rgba(255,255,255,0.14)" strokeWidth="3" />
                                    <path d="M15 3 a12 12 0 0 1 12 12" fill="none" stroke="#7ab3f0" strokeWidth="3" strokeLinecap="round" />
                                </svg>
                            </div>
                        </>
                    ) : displayedVideos.length ? (
                        <>
                            <div className={styles.playOverlay}>
                                <button
                                    type="button"
                                    className={`${styles.playOverlayBtn} ${isPlaying ? styles.overlayPlaying : styles.overlayPaused}`}
                                    onClick={handleTogglePlay}
                                    aria-label={isPlaying ? 'Pause' : 'Play'}
                                >
                                    {isPlaying
                                        ? <span className={styles.pauseOverlayIcon}><span className={styles.pauseBar} /><span className={styles.pauseBar} /></span>
                                        : <span className={styles.playOverlayIcon} />}
                                </button>
                            </div>
                            {displayedVideos.map((video, index) => (
                                <div className={styles.camPane} key={`${currentEpisodeId}-${video.object_name || index}`}>
                                    <span className={styles.camLabel}>{getVideoLabel(video, index)}</span>
                                    <video
                                        ref={(element) => { videoRefs.current[index] = element; }}
                                        className={styles.video}
                                        preload="auto"
                                        playsInline
                                        onPlay={index === 0 ? () => setIsPlaying(true) : undefined}
                                        onPause={index === 0 ? () => setIsPlaying(false) : undefined}
                                        onEnded={index === 0 ? () => {
                                            if (episodePlayStartRef.current !== null) {
                                                const elapsed = (Date.now() - episodePlayStartRef.current) / 1000;
                                                episodeWatchedSecondsRef.current[currentEpisodeId] =
                                                    (episodeWatchedSecondsRef.current[currentEpisodeId] || 0) + elapsed;
                                                episodePlayStartRef.current = null;
                                            }
                                        } : undefined}
                                        onLoadedMetadata={index === 0 ? (e) => setDuration(e.currentTarget.duration || 0) : undefined}
                                        onTimeUpdate={index === 0 ? (e) => setCurrentTime(e.currentTarget.currentTime || 0) : undefined}
                                        onVolumeChange={index === 0 ? (e) => setIsMuted(e.currentTarget.muted) : undefined}

                                    >
                                        <source src={video.signed_url} type="video/mp4" />
                                    </video>
                                </div>
                            ))}
                        </>
                    ) : (
                        <div className={styles.noVideo}>No sample video available.</div>
                    )}
                </div>

                {/* Playback controls bar */}
                <div className={styles.viewerControls}>
                    <div className={styles.ctrlLeft}>
                        <button type="button" className={styles.playBtn} onClick={handleTogglePlay} disabled={isLoadingContent}>
                            {isPlaying
                                ? <span className={styles.pauseIcon} />
                                : <span className={styles.playIcon} />}
                        </button>
                        <span className={styles.ctrlTime}>{formatTime(currentTime)}</span>
                        <button
                            type="button"
                            className={`${styles.ctrlSpeed}${(currentEpisodeState.speed) === 2 ? ` ${styles.ctrlSpeedFast}` : (currentEpisodeState.speed === 1.5) ? ` ${styles.ctrlSpeedMid}` : (currentEpisodeState.speed === 0.5) ? ` ${styles.ctrlSpeedSlow}` : ''}`}
                            onClick={cycleSpeed}
                            disabled={isLoadingContent}
                        >
                            {(currentEpisodeState.speed || 1).toFixed(1)}×
                        </button>
                    </div>
                    <div className={styles.seekWrap}>
                        <input
                            className={styles.seek}
                            type="range"
                            min="0"
                            max={duration || 0}
                            step="0.1"
                            value={Math.min(currentTime, duration || 0)}
                            onChange={handleSeek}
                            disabled={isLoadingContent}
                        />
                    </div>
                    <div className={styles.ctrlRight}>
                        <span className={styles.ctrlTime}>{formatTime(duration)}</span>
                        <button type="button" className={styles.ctrlIconBtn} onClick={handleToggleMute} disabled={isLoadingContent}>
                            {isMuted ? '🔇' : '🔊'}
                        </button>
                        <button type="button" className={styles.ctrlIconBtn} onClick={handleToggleFullscreen} disabled={isLoadingContent}>⛶</button>
                        <button
                            type="button"
                            className={`${styles.motionToggleBtn}${motionOpen ? ` ${styles.motionToggleBtnActive}` : ''}`}
                            onClick={() => setMotionOpen(o => !o)}
                            disabled={isLoadingContent}
                        >
                            <svg width="13" height="13" viewBox="0 0 13 13" fill="none">
                                <path d="M1 9 C2.5 9 2.5 4 4 4 S5.5 8 7 8 S8.5 5 10 5 S11.5 7 13 7" stroke="currentColor" strokeWidth="1.1" strokeLinecap="round" fill="none" />
                            </svg>
                            Motion data
                            <svg className={`${styles.motionChevron}${motionOpen ? ` ${styles.motionChevronOpen}` : ''}`} width="9" height="5" viewBox="0 0 9 5" fill="none">
                                <path d="M1 1l3.5 3L8 1" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                        </button>
                    </div>
                </div>

                <MotionDataPanel
                    episodeId={currentEpisodeId}
                    currentTime={currentTime}
                    isOpen={motionOpen}
                    data={jointStatesCache[currentEpisodeId]?.data ?? null}
                    loading={jointStatesCache[currentEpisodeId]?.status === 'loading'}
                    error={jointStatesCache[currentEpisodeId]?.status === 'error'}
                    onSeek={handleMotionSeek}
                />
            </div>

            {/* Bottom scoring panel */}
            <div className={styles.panel}>

                {/* Column 1: Episode meta + submit + stats */}
                <div className={styles.panelMeta}>
                    <div className={styles.panelHeader}>
                        <span className={styles.panelTitle}>Episode #{currentEpisodeId ?? '—'}</span>
                        <span className={styles.panelSub}>{currentScenario}</span>
                        {currentEpisode?.created_at
                            ? <span className={styles.panelSub}>{formatDate(currentEpisode.created_at)}</span>
                            : null}
                    </div>
                    <div className={styles.footerRow}>
                        <div className={styles.statCard}>
                            <div className={styles.statLbl}>Gate</div>
                            <div className={`${styles.statVal} ${gateClass}`}>{gateVote}</div>
                        </div>
                        <div className={styles.statCard}>
                            <div className={styles.statLbl}>Score</div>
                            <div className={`${styles.statVal} ${styles.statValBlue}`}>{scoreDisplay}</div>
                        </div>
                        <div className={styles.statCard}>
                            <div className={styles.statLbl}>Vote</div>
                            <div className={`${styles.statVal} ${voteClass}`}>{voteDisplay}</div>
                        </div>
                    </div>
                    {isExpertQa ? (
                        <div className={styles.expertQaBatchMeta}>
                            <span className={styles.expertQaBatchCount}>
                                Batch selected: {selectedBatchCount}
                            </span>
                            <span className={styles.expertQaBatchHint}>
                                Add any episodes you want, then submit the batch together.
                            </span>
                        </div>
                    ) : null}
                    <div className={styles.submitBtnWrap}>
                        <button
                            type="button"
                            className={styles.submitBtn}
                            onClick={isExpertQa && isCurrentEpisodeInBatch ? handleRemoveCurrentEpisodeFromBatch : handleTriggerSubmitReview}
                            disabled={isLoadingContent || submittingUploadReview || (!isExpertQa && !currentEpisodeSubmitReady) || (isExpertQa && !isCurrentEpisodeInBatch && !currentEpisodeSubmitReady)}
                        >
                            <svg width="12" height="12" viewBox="0 0 13 13" fill="none">
                                <path d="M2 6.5l3.5 3.5 5.5-6" stroke="#111" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                            {isLoadingContent ? 'Loading...' : submittingUploadReview ? 'Submitting...' : (isExpertQa ? (isCurrentEpisodeInBatch ? 'Remove from batch' : 'Add to batch') : 'Submit & earn points')}
                        </button>
                        {(!isLoadingContent && ((!isExpertQa && !currentEpisodeSubmitReady) || (isExpertQa && !isCurrentEpisodeInBatch && !currentEpisodeSubmitReady))) && (
                            <div className={styles.submitHoverTooltip}>
                                <svg width="11" height="11" viewBox="0 0 11 11" fill="none">
                                    <circle cx="5.5" cy="5.5" r="4.5" stroke="rgba(255,255,255,0.4)" strokeWidth="1" />
                                    <path d="M5.5 3.5v3M5.5 8v.5" stroke="rgba(255,255,255,0.4)" strokeWidth="1" strokeLinecap="round" />
                                </svg>
                                Complete all pass/fail &amp; quality ratings to submit
                            </div>
                        )}
                    </div>
                    {isExpertQa ? (
                        <div className={styles.expertQaActions}>
                            <button
                                type="button"
                                className={`${styles.submitBtn} ${styles.submitBtnSecondary}`}
                                onClick={handleFinalizeExpertQaBatch}
                                disabled={isLoadingContent || submittingUploadReview || selectedBatchCount <= 0 || !uploadRubricResult.ready}
                            >
                                {submittingUploadReview ? 'Submitting...' : `Submit selected episodes (${selectedBatchCount})`}
                            </button>
                            <button
                                type="button"
                                className={styles.clearBatchBtn}
                                onClick={handleClearExpertQaBatch}
                                disabled={isLoadingContent || submittingUploadReview || selectedBatchCount <= 0}
                            >
                                Clear batch
                            </button>
                        </div>
                    ) : null}
                </div>

                <div className={styles.vDivider} />

                {/* Column 2: Pass / Fail */}
                <div className={styles.scoreSection}>
                    <div className={styles.rLabel}>Pass / Fail</div>
                    <table className={styles.gridTable}>
                        <thead>
                            <tr>
                                <th className={styles.gridThLabel} />
                                <th className={styles.gridTh}>Pass</th>
                                <th className={styles.gridTh}>Fail</th>
                            </tr>
                        </thead>
                        <tbody>
                            {GATE_REQUIREMENTS.map((item) => {
                                const currentValue = currentEpisodeState.rubric?.[item.key] ?? '';
                                return (
                                    <tr key={item.key}>
                                        <td className={styles.gridTdLabel}>
                                            <TooltipQARequirements
                                                content={
                                                    <>
                                                    <div className={styles.tooltipTitle}>
                                                        {item.label}
                                                    </div>
                                                    <div className={styles.tooltipDesc}>
                                                        {item.tooltip}
                                                    </div>
                                                    </>
                                                }
                                                >
                                                <span className={styles.rowLabelText}>
                                                    {item.label}
                                                </span>
                                            </TooltipQARequirements>
                                        </td>
                                        {[{value: 'pass', cls: styles.dotPass}, {value: 'fail', cls: styles.dotFail}].map(({value, cls}) => (
                                            <td key={value} className={styles.gridTdCenter}>
                                                <span
                                                    className={`${styles.dot} ${currentValue === value ? cls : ''} ${isLoadingContent ? styles.dotDisabled : ''}`}
                                                    onClick={isLoadingContent ? undefined : () => handleRequirementChange(currentEpisodeId, item.key, currentValue === value ? '' : value)}
                                                />
                                            </td>
                                        ))}
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>

                <div className={styles.vDivider} />

                {/* Column 3: Quality scoring */}
                <div className={styles.scoreSection}>
                    <div className={styles.rLabel}>Quality</div>
                    <table className={styles.gridTable}>
                        <thead>
                            <tr>
                                <th className={styles.gridThLabel} />
                                {['Poor', 'Weak', 'OK', 'Good', 'Exc.'].map((h) => (
                                    <th key={h} className={styles.gridTh}>{h}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {EPISODE_SCORE_REQUIREMENTS.map((item) => {
                                const currentValue = String(currentEpisodeState.rubric?.[item.key] ?? '');
                                return (
                                    <tr key={item.key}>
                                        <td className={styles.gridTdLabel}>
                                             <TooltipQARequirements
                                                content={
                                                    <>
                                                    <div className={styles.tooltipTitle}>
                                                        {item.label}
                                                    </div>
                                                    <div className={styles.tooltipDesc}>
                                                        {item.tooltip}
                                                    </div>
                                                    </>
                                                }
                                                >
                                                <span className={styles.rowLabelText}>
                                                    {item.label}
                                                </span>
                                            </TooltipQARequirements>
                                        </td>
                                        {SCORE_LEVEL_OPTIONS.map((o, idx) => {
                                            const isSelected = currentValue === String(o.value);
                                            const dotCls = [styles.dotP1, styles.dotP2, styles.dotP3, styles.dotP4, styles.dotP5][idx];
                                            return (
                                                <td key={o.value} className={styles.gridTdCenter}>
                                                    <span
                                                        className={`${styles.dot} ${isSelected ? dotCls : ''} ${isLoadingContent ? styles.dotDisabled : ''}`}
                                                        onClick={isLoadingContent ? undefined : () => handleRequirementChange(currentEpisodeId, item.key, isSelected ? '' : String(o.value))}
                                                    />
                                                </td>
                                            );
                                        })}
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
                <ValidatorStatus
                    validatorAccessData={validatorAccessData}
                    onSelectionClick={() => setShowSelectionModal(true)}
                    onScoringClick={() => setShowScoringModal(true)}
                    onEarningsClick={() => setShowEarningsModal(true)}
                />
            </div>
        </div>
        { showValidatorAccessModal && 
            <ValidatorAccessModal
                validatorAccessData={validatorAccessData}
                onClose = {() => setShowValidatorAccessModal(false)}
                isSubmittingReview={submittingUploadReview}
                onSubmitReview={handleSubmitUploadReview}
            />
        }
        { showSelectionModal && (
            <SelectionModal
                onClose={() => setShowSelectionModal(false)}
            />
        )}
        { showScoringModal && (
            <ScoringModal
                onClose={() => setShowScoringModal(false)}
            />
        )}
        { showEarningsModal && (
            <EarningsModal
                onClose={() => setShowEarningsModal(false)}
            />
        )}
        { showQAReviewSuccessModal && (
            <QAReviewSuccessModal
                episodeId={currentEpisodeId}
                onContinue={handleLoadNextEpisodeAfterSubmit}
                onClose={() => setShowQAReviewSuccessModal(false)}
            />
        )}
        { isLeaveQAReviewModalOpen && (
            <LeaveQAReviewModalV2
                onStay={cancelLeaveQAReview}
                onLeave={() => {
                    try {
                        const stored = JSON.parse(localStorage.getItem('qaReviewState') || '{}');
                        stored[selectedUploadId] = {
                            ...stored[selectedUploadId],
                            reviewState,
                        };
                        localStorage.setItem('qaReviewState', JSON.stringify(stored));
                    } catch (e) {
                        // localStorage write failure is non-fatal
                    }
                    confirmLeaveQAReview();
                }}
                episodeIds={isExpertQa ? sampledEpisodeIds : effectiveSampledEpisodeIds}
                reviewedIds={Object.keys(locallySubmittedEpisodeIds).map(Number)}
                isExpertQa={isExpertQa}
            />
        )}
        { showRushQAReviewModal && 
            <RushQAReviewModal
                watchedSeconds={
                    (episodeWatchedSecondsRef.current[currentEpisodeId] || 0)
                    + (episodePlayStartRef.current !== null ? (Date.now() - episodePlayStartRef.current) / 1000 : 0)
                }
                episodeDurationSeconds={
                    currentEpisode?.video_duration_hours
                        ? Math.round(parseFloat(currentEpisode.video_duration_hours) * 3600)
                        : null
                }
                onClose={() => setShowRushQAReviewModal(false)}
            />
        }
        { showRateEpisodeVariationModal && (
            <RateEpisodeVariationModal
                episodeCount={effectiveSampledEpisodeIds.length}
                onConfirm={handleVariationConfirm}
                onClose={() => setShowRateEpisodeVariationModal(false)}
            />
        )}
        { customNotification && (
            <CustomNotification
                title={customNotification.title}
                description={customNotification.description}
                duration={customNotification.duration}
                onDismiss={() => {
                    setCustomNotification(null);
                    customNotification.onDismiss?.();
                }}
            />
        )}

        {preloadEpisodes.map((episode) =>
            sortEpisodeVideos(episode?.derived_videos || [], episode?.episode_id).map((video, index) => (
                <video
                    key={`preload-${episode.episode_id}-${index}`}
                    className={styles.preloadVideo}
                    src={video.signed_url}
                    preload="auto"
                    muted
                    playsInline
                />
            ))
        )}

        {firefoxNotificationNode}
 
        </>
    );
};

export default DataQAReview;
